import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  input,
  linkedSignal,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ShellEnvironment, SOFTLINE_TOKEN_SHELL_ENVIRONMENT } from '@softapps/core';
import { Modal, ResolvableModal, TranslatePipe } from '@softline/ui-core';
import { Observable, Subscription } from 'rxjs';
import { SOFTLINE_CONFIG_ARTIKEL_INFO_MODAL_COMPONENT } from '../../artikel.shared';
import { ArtikelInfoModalParams } from '../../types/artikel-info.modal';

@Component({
  selector: 'soft-artikel-info',
  imports: [CommonModule, TranslatePipe],
  templateUrl: './artikel-info.component.html',
  styleUrls: ['./artikel-info.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // The injected page renders <soft-page>, which cancels the shell's padding
    // with `margin: -8px -16px 0 -16px` and `height: calc(100% + 8px)`. A modal
    // has no shell padding to cancel, so those offsets pushed the page past the
    // dialog's clip box — slicing off the top 8px and the first 16px of every
    // full-bleed row ("Preise" rendered as "reise"). EMBEDDED is the built-in
    // opt-out: page.component.ts then uses margin 0 / height 100%.
    { provide: SOFTLINE_TOKEN_SHELL_ENVIRONMENT, useValue: ShellEnvironment.EMBEDDED },
  ],
})
export class ArtikelInfoComponent<T extends object>
  extends ResolvableModal<void>
  implements OnInit, OnDestroy, Modal<void>
{
  private subscription?: Subscription;

  id$!: Observable<number>;
  params!: ArtikelInfoModalParams<T>;
  close!: (_: void) => void;

  id = input<number>();
  options = input<ArtikelInfoModalParams<T>>();

  // inputs: Record<string, unknown> = {};

  readonly inputs = linkedSignal<Record<string, unknown>>(() => {
    const options = this.options();
    const id = this.id();
    if (!id) return {};

    return {
      id,
      params: options?.data ?? {},
    };
  });

  constructor(
    @Inject(SOFTLINE_CONFIG_ARTIKEL_INFO_MODAL_COMPONENT) readonly modalComponent
  ) {
    super();
  }

  registerCloseHandler(handler: (result: void) => void) {
    this.close = handler;
  }

  ngOnInit(): void {
    this.params?.onOpen?.();

    if (this.id$) {
      this.id$.subscribe((id) => {
        this.inputs.set({
          id,
          params: this.options()?.data ?? this.params?.data ?? {},
        });
      });
    }
  }

  ngOnDestroy(): void {
    this.params?.onClose?.();

    if (this.subscription && !this.subscription.closed) this.subscription.unsubscribe();

    this.subscription = undefined;
  }
}
